import { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiDownload, FiEye, FiFileText, FiClock, FiRefreshCw, FiImage, FiX, FiExternalLink, FiPlus, FiArchive, FiSearch, FiCircle } from 'react-icons/fi';
import { FaCircle } from 'react-icons/fa';
import { GoArrowLeft } from "react-icons/go";
import { MdOutlineMoreVert } from 'react-icons/md';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { 
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { 
    Pagination, 
    PaginationContent, 
    PaginationItem, 
    PaginationLink, 
    PaginationNext, 
    PaginationPrevious 
} from "@/components/ui/pagination";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { CreateTaskFromSubmissionDialog } from "@/components/CreateTaskFromSubmissionDialog";
import { TaskLink } from "@/components/ItemLink";

// TODO: Move this to a shared component
const MinimalBadge = ({ children, className }) => (
    <div className={cn(
        "rounded-sm border border-violet-200/50 bg-violet-50/50 px-2 py-0.5 text-[10px] font-medium uppercase text-violet-700 dark:border-violet-800/50 dark:bg-violet-950/50 dark:text-violet-300",
        className
    )}>
        {children}
    </div>
);

const FormSubmissions = () => {
    const { formSlug, organizationSlug, submissionId } = useParams();
    const { axiosInstance } = useAuth();
    const navigate = useNavigate();
    
    // Helper functions for form URL
    const getFormBaseUrl = () => {
        return import.meta.env.VITE_FORMS_FRONTEND_BASE_URL || 'https://forms.status.bot';
    };
    
    const getFormDisplayDomain = () => {
        const baseUrl = getFormBaseUrl();
        return baseUrl.replace(/^https?:\/\//, '');
    };
    
    const getFormFullUrl = (slug) => {
        const baseUrl = getFormBaseUrl();
        return `${baseUrl}/${slug}`;
    };
    
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState(null);
    const [submissions, setSubmissions] = useState([]);
    const [pagination, setPagination] = useState({
        page: 1,
        pages: 1,
        total: 0,
        limit: 20
    });
    const [selectedSubmission, setSelectedSubmission] = useState(null);
    const [submissionDetailsOpen, setSubmissionDetailsOpen] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [fileUrls, setFileUrls] = useState({});
    const [lightboxImage, setLightboxImage] = useState(null);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
    const [selectedSubmissionId, setSelectedSubmissionId] = useState(null);
    const [archiveReason, setArchiveReason] = useState('');
    const [showArchived, setShowArchived] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedSubmissionForTask, setSelectedSubmissionForTask] = useState(null);
    const [createTaskFromSubmissionOpen, setCreateTaskFromSubmissionOpen] = useState(false);
    const [loadingSubmissionId, setLoadingSubmissionId] = useState(null);

    // Get non-markdown components for display
    const getDisplayableComponents = () => {
        if (!form?.components) return [];
        return form.components.filter(c => c.type !== 'markdown');
    };

    const fetchSubmissions = async (page = 1) => {
        setLoading(true);
        setRefreshing(true);
        try {
            const params = new URLSearchParams({
                page: page.toString(),
                ...(showArchived && { includeArchived: 'true' }),
                ...(searchTerm.trim() && { search: searchTerm.trim() })
            });
            
            const response = await axiosInstance.get(`/forms/${formSlug}/submissions?${params}`);
            setSubmissions(response.data.submissions);
            setPagination(response.data.pagination);
            
            // If we don't have form details yet, fetch them
            if (!form) {
                const formResponse = await axiosInstance.get(`/forms/edit/${formSlug}`);
                setForm(formResponse.data);
            }
        } catch (error) {
            console.error('Error fetching submissions:', error);
            toast.error('Error loading submissions. Please try again later.');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    // Handle URL-based submission viewing
    useEffect(() => {
        if (submissionId && !submissionDetailsOpen) {
            viewSubmissionDetails(submissionId);
        } else if (!submissionId && submissionDetailsOpen) {
            // When URL doesn't have submissionId but dialog is open, just close the dialog state
            // Don't call closeSubmissionDetails() here to avoid navigation loop
            setSubmissionDetailsOpen(false);
            setSelectedSubmission(null);
        }
    }, [submissionId]);

    useEffect(() => {
        fetchSubmissions();
    }, [formSlug]);

    // Refetch when filters change
    useEffect(() => {
        // Debounce search to avoid too many API calls
        const timeoutId = setTimeout(() => {
            if (pagination.page === 1) {
                fetchSubmissions(1);
            } else {
                setPagination(prev => ({ ...prev, page: 1 }));
                fetchSubmissions(1);
            }
        }, searchTerm ? 300 : 0); // 300ms debounce for search, immediate for other filters

        return () => clearTimeout(timeoutId);
    }, [showArchived, searchTerm]);

    const handlePageChange = (page) => {
        fetchSubmissions(page);
    };

    const viewSubmissionDetails = async (submissionId) => {
        setLoadingSubmissionId(submissionId);
        try {
            const response = await axiosInstance.get(`/forms/submissions/${submissionId}`);
            setSelectedSubmission(response.data);
            setSubmissionDetailsOpen(true);
            
            // Update URL to include submission ID
            navigate(`/${organizationSlug}/forms/${formSlug}/submissions/${submissionId}`, { replace: true });
        } catch (error) {
            console.error('Error fetching submission details:', error);
            toast.error('Error loading submission details');
            
            // If there was an error loading the submission, navigate back to the list
            if (window.location.pathname.includes(submissionId)) {
                navigate(`/${organizationSlug}/forms/${formSlug}/submissions`, { replace: true });
            }
        } finally {
            setLoadingSubmissionId(null);
        }
    };

    const closeSubmissionDetails = () => {
        setSubmissionDetailsOpen(false);
        setSelectedSubmission(null);
        
        // Navigate back to submissions list
        navigate(`/${organizationSlug}/forms/${formSlug}/submissions`, { replace: true });
    };

    const archiveSubmission = async (submissionId, reason = '') => {
        try {
            await axiosInstance.patch(`/forms/submissions/${submissionId}/archive`, {
                reason: reason.trim()
            });
            toast.success('Submission archived successfully');
            // Refresh the submissions list
            fetchSubmissions(pagination.page);
            // Reset dialog state
            setArchiveDialogOpen(false);
            setSelectedSubmissionId(null);
            setArchiveReason('');
        } catch (error) {
            console.error('Error archiving submission:', error);
            toast.error('Error archiving submission');
        }
    };

    const openArchiveDialog = (submissionId) => {
        setSelectedSubmissionId(submissionId);
        setArchiveDialogOpen(true);
    };

    const openCreateTaskDialog = (submission) => {
        setSelectedSubmissionForTask(submission);
        setCreateTaskFromSubmissionOpen(true);
    };

    const handleArchiveConfirm = () => {
        if (selectedSubmissionId) {
            archiveSubmission(selectedSubmissionId, archiveReason);
        }
    };

    // Helper function to generate file description (same logic as table display)
    const getFileDescription = (files) => {
        if (!Array.isArray(files) || files.length === 0) {
            return "No files uploaded";
        }
        
        // Count images vs other files
        const imageFiles = files.filter(isImageFile);
        const otherFiles = files.filter(file => !isImageFile(file));
        
        // Generate simple text description
        if (imageFiles.length > 0 && otherFiles.length > 0) {
            // Mixed files
            return `${imageFiles.length} image${imageFiles.length === 1 ? '' : 's'}, ${otherFiles.length} file${otherFiles.length === 1 ? '' : 's'}`;
        } else if (imageFiles.length > 0) {
            // Only images
            return `${imageFiles.length} image${imageFiles.length === 1 ? '' : 's'} uploaded`;
        } else {
            // Only other files
            return `${otherFiles.length} file${otherFiles.length === 1 ? '' : 's'} uploaded`;
        }
    };

    // Helper function to generate submission URL
    const getSubmissionUrl = (submissionId) => {
        const baseUrl = window.location.origin;
        return `${baseUrl}/${organizationSlug}/forms/${formSlug}/submissions/${submissionId}`;
    };

    // CSV sanitization helper function
    const sanitizeCSVValue = (value) => {
        if (value === null || value === undefined) {
            return '';
        }
        
        let sanitized = String(value);
        
        // Remove or replace problematic characters
        sanitized = sanitized
            .replace(/\r?\n/g, ' ') // Replace newlines with spaces
            .replace(/\r/g, ' ')    // Replace carriage returns with spaces
            .trim();                // Remove leading/trailing whitespace
        
        // If the value contains commas, quotes, or starts/ends with whitespace, wrap in quotes
        if (sanitized.includes(',') || sanitized.includes('"') || sanitized.includes('\n')) {
            // Escape existing quotes by doubling them
            sanitized = sanitized.replace(/"/g, '""');
            // Wrap in quotes
            sanitized = `"${sanitized}"`;
        }
        
        return sanitized;
    };

    const downloadAsCSV = () => {
        // Create header row based on form components
        const headerRow = ["Submission ID", "Date & Time"];
        
        // Add field headers
        if (form && form.components) {
            form.components.forEach(component => {
                if (component.type !== 'markdown') {
                    headerRow.push(sanitizeCSVValue(component.name));
                }
            });
        }
        
        // Prepare the CSV content
        let csvContent = "data:text/csv;charset=utf-8," + headerRow.join(",") + "\n";
        
        // Add data rows
        submissions.forEach(submission => {
            const row = [
                sanitizeCSVValue(submission.id),
                sanitizeCSVValue(new Date(submission.createdAt).toLocaleString('en-US', {
                    year: 'numeric',
                    month: 'numeric',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                    second: '2-digit',
                    hour12: true
                }))
            ];
            
            // Add field values
            if (form && form.components) {
                form.components.forEach(component => {
                    if (component.type !== 'markdown') {
                        const value = submission.data[component.name];
                        if (value === undefined || value === null) {
                            row.push("");
                        } else if (Array.isArray(value)) {
                            // For file uploads, create a description with link to submission
                            if (value.length === 0) {
                                row.push("");
                            } else {
                                const description = getFileDescription(value);
                                const submissionUrl = getSubmissionUrl(submission.id);
                                const linkText = `${description} - View: ${submissionUrl}`;
                                row.push(sanitizeCSVValue(linkText));
                            }
                        } else if (typeof value === 'object') {
                            row.push(sanitizeCSVValue(JSON.stringify(value)));
                        } else {
                            row.push(sanitizeCSVValue(value));
                        }
                    }
                });
            }
            
            csvContent += row.join(",") + "\n";
        });
        
        // Create download link
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `${sanitizeCSVValue(form?.name || formSlug)}_submissions.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Check if a file is an image based on contentType
    const isImageFile = (file) => {
        return file.contentType && file.contentType.startsWith('image/');
    };

    // Get file URL (cached)
    const getFileUrl = async (file) => {
        if (fileUrls[file.key]) {
            return fileUrls[file.key];
        }

        try {
            const response = await axiosInstance.get(`/forms/files/${file.key}`);
            const url = response.data.url;
            
            // Cache the URL
            setFileUrls(prev => ({
                ...prev,
                [file.key]: url
            }));
            
            return url;
        } catch (error) {
            console.error('Error getting file URL:', error);
            return null;
        }
    };

    // Try to download a file attachment
    const downloadFile = async (file) => {
        try {
            // Create a pre-signed URL for download
            const url = await getFileUrl(file);
            if (url) {
                // Open the URL in a new tab
                window.open(url, '_blank');
            } else {
                toast.error('Error generating file URL');
            }
        } catch (error) {
            console.error('Error downloading file:', error);
            toast.error('Error downloading file');
        }
    };

    // Format a submission value for display in the table
    const formatValueForTable = (value, component, submission = null) => {
        if (value === undefined || value === null) {
            return <span className="text-muted-foreground">—</span>;
        }
        
        if (Array.isArray(value)) {
            // File uploads
            if (value.length === 0) return <span className="text-muted-foreground">No files uploaded</span>;
            
            // Count images vs other files
            const imageFiles = value.filter(isImageFile);
            const otherFiles = value.filter(file => !isImageFile(file));
            const totalFiles = value.length;
            
            // Generate simple text description
            let description = "";
            if (imageFiles.length > 0 && otherFiles.length > 0) {
                // Mixed files
                description = `${imageFiles.length} image${imageFiles.length === 1 ? '' : 's'}, ${otherFiles.length} file${otherFiles.length === 1 ? '' : 's'}`;
            } else if (imageFiles.length > 0) {
                // Only images
                description = `${imageFiles.length} image${imageFiles.length === 1 ? '' : 's'} uploaded`;
            } else {
                // Only other files
                description = `${otherFiles.length} file${otherFiles.length === 1 ? '' : 's'} uploaded`;
            }
            
            // Make it clickable if we have a submission
            if (submission) {
                return (
                    <button 
                        className="text-sm text-muted-foreground hover:text-foreground hover:underline transition-colors text-left"
                        onClick={(e) => {
                            e.stopPropagation();
                            viewSubmissionDetails(submission.id);
                        }}
                        title="Click to view files"
                    >
                        {description}
                    </button>
                );
            }
            
            return <span className="text-sm text-muted-foreground">{description}</span>;
        }
        
        if (typeof value === 'object') {
            return <span className="text-muted-foreground italic">Object</span>;
        }
        
        // Truncate long values
        const strValue = String(value);
        if (strValue.length > 100) {
            return strValue.substring(0, 100) + '...';
        }
        
        return strValue;
    };

    // Small image preview for table cells
    const TableImagePreview = ({ file, getFileUrl }) => {
        const [imageUrl, setImageUrl] = useState(null);
        const [loading, setLoading] = useState(true);
        
        useEffect(() => {
            let mounted = true;
            
            const loadImage = async () => {
                try {
                    const url = await getFileUrl(file);
                    if (mounted && url) {
                        setImageUrl(url);
                    }
                } catch (err) {
                    console.error('Error loading image thumbnail:', err);
                } finally {
                    if (mounted) {
                        setLoading(false);
                    }
                }
            };
            
            loadImage();
            
            return () => {
                mounted = false;
            };
        }, [file]);
        
        const handleClick = async (e) => {
            e.stopPropagation();
            try {
                const url = imageUrl || await getFileUrl(file);
                if (url) {
                    setLightboxImage({ 
                        url, 
                        alt: file.filename,
                        file
                    });
                    setLightboxOpen(true);
                }
            } catch (err) {
                console.error('Error opening image:', err);
            }
        };
        
        if (loading || !imageUrl) {
            return <div className="w-8 h-8 bg-muted animate-pulse rounded-md flex-shrink-0" />;
        }
        
        return (
            <div 
                className="w-8 h-8 rounded-md overflow-hidden flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
                title={`View ${file.filename}`}
                onClick={handleClick}
            >
                <img 
                    src={imageUrl} 
                    alt={file.filename} 
                    className="w-full h-full object-cover" 
                />
            </div>
        );
    };

    // Table thumbnails grid component for file previews
    const TableFilePreviews = ({ files, imageFiles, otherFiles, totalFiles, getFileUrl }) => {
        // Show the first 3 files (prioritizing images)
        const filesToShow = [...imageFiles, ...otherFiles].slice(0, 3);
        const remainingCount = totalFiles - filesToShow.length;
        
        // If only one file and it's an image, make it slightly larger
        if (totalFiles === 1 && imageFiles.length === 1) {
            return (
                <div className="flex items-center gap-2">
                    <div className="relative rounded-md border border-border overflow-hidden w-10 h-10">
                        <TableImagePreview file={imageFiles[0]} getFileUrl={getFileUrl} />
                    </div>
                    <span className="text-xs truncate max-w-[120px]" title={imageFiles[0].filename}>
                        {imageFiles[0].filename}
                    </span>
                </div>
            );
        }
        
        return (
            <div className="flex items-center gap-2">
                {/* Thumbnail images */}
                <div className="flex -space-x-2">
                    {filesToShow.map((file, index) => (
                        <div 
                            key={index} 
                            className="relative rounded-md border border-border overflow-hidden z-10" 
                            style={{ zIndex: 10 - index }}
                        >
                            {isImageFile(file) ? (
                                <TableImagePreview file={file} getFileUrl={getFileUrl} />
                            ) : (
                                <div className="w-8 h-8 bg-muted/50 flex items-center justify-center text-xs font-medium text-muted-foreground">
                                    {file.contentType?.includes('pdf') ? '📄' : 
                                     file.contentType?.includes('word') ? '📝' : 
                                     file.contentType?.includes('excel') ? '📊' : '📎'}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
                
                {/* Count indicators */}
                <div className="flex items-center">
                    {remainingCount > 0 && (
                        <Badge variant="secondary" className="text-xs font-normal">
                            +{remainingCount} more
                        </Badge>
                    )}
                    
                    {filesToShow.length > 0 && totalFiles > 1 && (
                        <Badge variant="outline" className="ml-1 text-xs font-normal">
                            {totalFiles} files
                        </Badge>
                    )}
                </div>
            </div>
        );
    };

    // File card component for displaying files and images
    const FileCard = ({ file, getFileUrl, isImage, onDownload, onViewImage }) => {
        const [imageUrl, setImageUrl] = useState(null);
        const [loading, setLoading] = useState(isImage);
        
        useEffect(() => {
            if (isImage) {
                const loadImage = async () => {
                    try {
                        const url = await getFileUrl(file);
                        if (url) {
                            setImageUrl(url);
                        }
                    } catch (err) {
                        console.error('Error loading image thumbnail:', err);
                    } finally {
                        setLoading(false);
                    }
                };
                
                loadImage();
            }
        }, [file, isImage, getFileUrl]);
        
        const handleDownload = (e) => {
            e.stopPropagation();
            onDownload(file);
        };
        
        const handleImageClick = () => {
            if (isImage && imageUrl && onViewImage) {
                onViewImage(imageUrl);
            }
        };
        
        const getIconForFile = () => {
            if (file.contentType?.includes('pdf')) return '📄';
            if (file.contentType?.includes('word') || file.contentType?.includes('document')) return '📝';
            if (file.contentType?.includes('spreadsheet') || file.contentType?.includes('excel')) return '📊';
            if (file.contentType?.includes('zip') || file.contentType?.includes('compressed')) return '📦';
            if (file.contentType?.startsWith('video/')) return '🎬';
            if (file.contentType?.startsWith('audio/')) return '🎵';
            return '📎';
        };
        
        return (
            <div className={`group rounded-md border bg-card shadow-sm overflow-hidden transition-all hover:shadow-md ${isImage ? 'cursor-zoom-in' : ''}`}>
                {isImage ? (
                    <div className="aspect-square bg-muted relative" onClick={handleImageClick}>
                        {loading ? (
                            <div className="absolute inset-0 flex items-center justify-center">
                                <div className="h-10 w-10 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
                            </div>
                        ) : imageUrl ? (
                            <div className="relative h-full w-full">
                                <img 
                                    src={imageUrl} 
                                    alt={file.filename} 
                                    className="w-full h-full object-cover" 
                                />
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                                    <FiEye className="h-6 w-6 text-white opacity-0 group-hover:opacity-90 transition-opacity drop-shadow-md" />
                                </div>
                                <Button 
                                    variant="ghost" 
                                    size="sm"
                                    className="absolute bottom-2 right-2 h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity bg-black/40 hover:bg-black/60 text-white"
                                    onClick={handleDownload}
                                >
                                    <FiDownload className="h-3 w-3" />
                                </Button>
                            </div>
                        ) : (
                            <div className="w-full h-full flex items-center justify-center bg-muted/20">
                                <FiImage className="h-10 w-10 text-muted-foreground" />
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="aspect-square p-4 bg-muted/30 flex flex-col items-center justify-center gap-2">
                        <div className="text-4xl">{getIconForFile()}</div>
                        <Button 
                            variant="outline" 
                            size="sm" 
                            className="mt-2 h-7"
                            onClick={handleDownload}
                        >
                            <FiDownload className="h-3 w-3 mr-1" />
                            <span className="text-xs">Download</span>
                        </Button>
                    </div>
                )}
                <div className="p-2">
                    <div className="text-xs truncate font-medium" title={file.filename}>
                        {file.filename}
                    </div>
                    <div className="text-[10px] text-muted-foreground">
                        {(file.size / 1024).toFixed(1)} KB
                    </div>
                </div>
            </div>
        );
    };

    // Format a submission value for the details dialog
    const formatValueForDetails = (value, component) => {
        if (value === undefined || value === null) {
            return <span className="text-muted-foreground">—</span>;
        }
        
        if (Array.isArray(value)) {
            // File uploads
            if (value.length === 0) return <span className="text-muted-foreground">—</span>;
            
            return (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-2">
                    {value.map((file, index) => (
                        <FileCard 
                            key={index}
                            file={file}
                            getFileUrl={getFileUrl}
                            isImage={isImageFile(file)}
                            onDownload={downloadFile}
                            onViewImage={(url) => {
                                setLightboxImage({ 
                                    url, 
                                    alt: file.filename,
                                    file
                                });
                                setLightboxOpen(true);
                            }}
                        />
                    ))}
                </div>
            );
        }
        
        // Check if value is an object with a 'value' property (common pattern in form data)
        if (typeof value === 'object' && value !== null) {
            // If the object has a value property, just show that value
            if (value.hasOwnProperty('value')) {
                return formatValueForDetails(value.value, component);
            }
            
            // Otherwise format the object as JSON
            return <pre className="text-sm bg-muted/50 p-2 rounded-md overflow-x-auto mt-1">{JSON.stringify(value, null, 2)}</pre>;
        }
        
        // Handle different types of values
        return <span className="text-base">{String(value)}</span>;
    };

    const fadeInVariants = {
        hidden: { opacity: 0, scale: 0.95 },
        visible: { opacity: 1, scale: 1 }
    };

    return (
        <Layout>
            <motion.div
                variants={fadeInVariants}
                initial="hidden"
                animate="visible"
                transition={{ duration: 0.5 }}
                className="space-y-6 mb-8"
            >
                <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4 mb-6">
                    {/* Left side - Title and info */}
                    <div className="flex-col items-start space-y-2">
                        <div className="flex items-center space-x-4">
                            <div 
                                className="flex items-center space-x-4 cursor-pointer text-3xl group"
                                onClick={() => navigate('/forms')}
                            >
                                <GoArrowLeft className="group-hover:text-violet-500 transition-colors" />
                                {form ? (
                                    <h1 className="-mt-0.5 font-semibold">{form.name}</h1>
                                ) : (
                                    <Skeleton className="h-9 w-48" />
                                )}
                            </div>
                            {form && (
                                <TooltipProvider>
                                    <Tooltip>
                                        <TooltipTrigger>
                                            {form.online ? (
                                                <Badge variant="outline" className="border-emerald-200 text-emerald-700 dark:border-emerald-700 dark:text-emerald-300 text-xs px-3 py-1 flex items-center space-x-1">
                                                    <FaCircle className="h-2 w-2" />
                                                    <span>Online</span>
                                                </Badge>
                                            ) : (
                                                <Badge variant="outline" className="border-muted-foreground/30 text-muted-foreground text-xs px-3 py-1 flex items-center space-x-1">
                                                    <FiCircle className="h-2 w-2" />
                                                    <span>Offline</span>
                                                </Badge>
                                            )}
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            {form.online ? 'Form is accepting submissions' : 'Form is not accepting submissions'}
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            )}
                        </div>
                        <div className="flex items-center space-x-2 pl-12">
                            <a 
                                href={getFormFullUrl(formSlug)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-muted-foreground -mt-0.5 hover:text-primary flex items-center gap-1 transition-colors"
                            >
                                {getFormDisplayDomain()}/{formSlug}
                                <FiExternalLink className="h-3 w-3" />
                            </a>
                            <span className="text-muted-foreground">•</span>
                            <span className="text-sm text-muted-foreground">
                                {pagination.total} {pagination.total === 1 ? 'submission' : 'submissions'}
                            </span>
                            {/* Active filter indicators */}
                            {(searchTerm || showArchived) && (
                                <div className="flex items-center gap-3">
                                    {searchTerm && (
                                        <div className="flex items-center gap-1.5 text-xs text-violet-600 dark:text-violet-400">
                                            <div className="w-1.5 h-1.5 rounded-full bg-violet-500"></div>
                                            <span>Searching "{searchTerm.length > 20 ? searchTerm.substring(0, 20) + '...' : searchTerm}"</span>
                                        </div>
                                    )}
                                    {showArchived && (
                                        <div className="flex items-center gap-1.5 text-xs text-orange-600 dark:text-orange-400">
                                            <div className="w-1.5 h-1.5 rounded-full bg-orange-500"></div>
                                            <span>Including archived</span>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                    
                    {/* Right side - Search, filters, and actions */}
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                        {/* Search and Archive Toggle */}
                        <div className="flex items-center gap-2">
                            {/* Search */}
                            <div className="relative">
                                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                                <Input
                                    placeholder="Search submissions..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="pl-10 pr-8 h-9 w-full sm:w-56 lg:w-64 text-sm transition-all duration-200 sm:focus:w-72"
                                />
                                {searchTerm && (
                                    <button
                                        onClick={() => setSearchTerm('')}
                                        className="absolute right-2 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                                    >
                                        <FiX className="h-3 w-3" />
                                    </button>
                                )}
                            </div>
                            
                            {/* Archive Toggle */}
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <button 
                                            onClick={() => setShowArchived(!showArchived)}
                                            className={cn(
                                                "flex items-center gap-2 px-3 py-2 rounded-md border transition-all duration-200 whitespace-nowrap",
                                                showArchived 
                                                    ? "border-orange-300 bg-orange-50 text-orange-700 dark:border-orange-700 dark:bg-orange-950/50 dark:text-orange-300" 
                                                    : "border-muted-foreground/20 hover:border-muted-foreground/40 bg-background"
                                            )}
                                        >
                                            <FiArchive className="h-4 w-4" />
                                            <span className="text-xs font-medium hidden sm:inline">Archived</span>
                                        </button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        {showArchived ? 'Hide archived submissions' : 'Show archived submissions'}
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        </div>
                        
                        {/* Action Buttons */}
                        <div className="flex items-center gap-2">
                            <Button 
                                variant="secondary" 
                                size="sm"
                                onClick={() => fetchSubmissions(pagination.page)}
                                disabled={refreshing}
                                className="h-9 w-9 p-0"
                            >
                                <FiRefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                            </Button>
                            <Button 
                                variant="secondary"
                                onClick={downloadAsCSV}
                                disabled={submissions.length === 0}
                                size="sm"
                                className="h-9"
                            >
                                <FiDownload className="mr-2 h-4 w-4" />
                                <span className="hidden sm:inline">Export CSV</span>
                                <span className="sm:hidden">CSV</span>
                            </Button>
                        </div>
                    </div>
                </div>

                <motion.div 
                    variants={fadeInVariants}
                    transition={{ delay: 0.2 }}
                >
                    {loading && submissions.length === 0 ? (
                        <Card className="shadow-sm">
                            <CardContent className="p-6">
                                <div className="space-y-3">
                                    <Skeleton className="h-8 w-full" />
                                    {[...Array(4)].map((_, i) => (
                                        <Skeleton key={i} className="h-16 w-full" />
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    ) : submissions.length > 0 ? (
                        <>
                            <div className="relative w-full overflow-auto">
                                <div className="w-full min-w-full rounded-md border bg-card shadow-sm">
                                    <Table className="w-full caption-bottom">
                                        <TableHeader>
                                            <TableRow className="group hover:bg-accent transition-none">
                                                <TableHead className="w-[180px] min-w-[180px]">Date & Time</TableHead>
                                                {getDisplayableComponents().map(component => (
                                                    <TableHead 
                                                        key={component._id}
                                                        className="min-w-[150px] max-w-[300px] whitespace-nowrap"
                                                    >
                                                        {component.name}
                                                    </TableHead>
                                                ))}
                                                <TableHead className="w-[100px] min-w-[100px] text-right sticky right-0 bg-card z-[1] group-hover:bg-accent">
                                                    <div className="absolute top-0 -left-4 w-4 h-full bg-gradient-to-r from-transparent to-card group-hover:bg-gradient-to-r group-hover:from-transparent group-hover:to-accent"></div>
                                                    Actions
                                                </TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {submissions.map((submission) => (
                                                <TableRow 
                                                    key={submission.id} 
                                                    className={`group hover:bg-accent transition-none`}
                                                >
                                                    <TableCell className="font-medium">
                                                        <div className="flex items-center gap-2">
                                                            <span className="truncate">
                                                                {new Date(submission.createdAt).toLocaleString()}
                                                            </span>
                                                            {submission.archived && (
                                                                <Badge variant="outline" className="text-xs border-orange-300 bg-orange-50 text-orange-700 dark:border-orange-700 dark:bg-orange-950/50 dark:text-orange-300">
                                                                    Archived
                                                                </Badge>
                                                            )}
                                                            {submission.linkedTasks && submission.linkedTasks.length > 0 && (
                                                                <Badge variant="outline" className="text-xs border-violet-300 bg-violet-50 text-violet-700 dark:border-violet-700 dark:bg-violet-950/50 dark:text-violet-300 whitespace-nowrap">
                                                                    {submission.linkedTasks.length} Task{submission.linkedTasks.length === 1 ? '' : 's'}
                                                                </Badge>
                                                            )}
                                                        </div>
                                                    </TableCell>
                                                    {getDisplayableComponents().map(component => (
                                                        <TableCell 
                                                            key={component._id} 
                                                            className="truncate"
                                                        >
                                                            <div className="max-w-full overflow-hidden text-ellipsis">
                                                                {formatValueForTable(
                                                                    submission.data[component.name], 
                                                                    component,
                                                                    submission
                                                                )}
                                                            </div>
                                                        </TableCell>
                                                    ))}
                                                    <TableCell className="text-right sticky right-0 bg-card group-hover:bg-accent z-[1]">
                                                        <div className="absolute top-0 -left-4 w-4 h-full bg-gradient-to-r from-transparent to-card group-hover:bg-gradient-to-r group-hover:from-transparent group-hover:to-accent"></div>
                                                        <div className="flex gap-1 justify-end">
                                                            <TooltipProvider delayDuration={0}>
                                                                <Tooltip>
                                                                    <TooltipTrigger asChild>
                                                                        <Button 
                                                                            variant="ghost" 
                                                                            size="sm"
                                                                            className="h-8 w-8 p-0 hover:bg-card"
                                                                            onClick={() => viewSubmissionDetails(submission.id)}
                                                                            disabled={loadingSubmissionId === submission.id}
                                                                        >
                                                                            {loadingSubmissionId === submission.id ? (
                                                                                <div className="h-4 w-4 rounded-full border-2 border-muted-foreground/30 border-t-foreground animate-spin" />
                                                                            ) : (
                                                                                <FiEye className="h-4 w-4" />
                                                                            )}
                                                                            <span className="sr-only">View Details</span>
                                                                        </Button>
                                                                    </TooltipTrigger>
                                                                    <TooltipContent>
                                                                        View Details
                                                                    </TooltipContent>
                                                                </Tooltip>
                                                            </TooltipProvider>
                                                            
                                                            <DropdownMenu>
                                                                <TooltipProvider delayDuration={0}>
                                                                    <Tooltip>
                                                                        <TooltipTrigger asChild>
                                                                            <DropdownMenuTrigger asChild>
                                                                                <Button 
                                                                                    variant="ghost" 
                                                                                    size="sm"
                                                                                    className="h-8 w-8 p-0 hover:bg-card"
                                                                                >
                                                                                    <MdOutlineMoreVert className="h-4 w-4" />
                                                                                    <span className="sr-only">More actions</span>
                                                                                </Button>
                                                                            </DropdownMenuTrigger>
                                                                        </TooltipTrigger>
                                                                        <TooltipContent>
                                                                            More actions
                                                                        </TooltipContent>
                                                                    </Tooltip>
                                                                </TooltipProvider>
                                                                <DropdownMenuContent align="end">
                                                                    {!submission.archived ? (
                                                                        <DropdownMenuItem
                                                                            onClick={() => openArchiveDialog(submission.id)}
                                                                        >
                                                                            <FiArchive className="mr-2 h-4 w-4" />
                                                                            Archive Submission
                                                                        </DropdownMenuItem>
                                                                    ) : (
                                                                        <DropdownMenuItem disabled>
                                                                            <FiArchive className="mr-2 h-4 w-4" />
                                                                            Already Archived
                                                                        </DropdownMenuItem>
                                                                    )}
                                                                    <DropdownMenuItem
                                                                        onClick={() => openCreateTaskDialog(submission)}
                                                                    >
                                                                        <FiPlus className="mr-2 h-4 w-4" />
                                                                        New Task From Submission
                                                                    </DropdownMenuItem>
                                                                </DropdownMenuContent>
                                                            </DropdownMenu>
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </div>
                            </div>
                            
                            {pagination.pages > 1 && (
                                <div className="mt-4 flex justify-center">
                                    <Pagination>
                                        <PaginationContent>
                                            {pagination.page > 1 && (
                                                <PaginationItem>
                                                    <PaginationPrevious 
                                                        onClick={() => handlePageChange(pagination.page - 1)} 
                                                    />
                                                </PaginationItem>
                                            )}
                                            
                                            {[...Array(pagination.pages)].map((_, i) => {
                                                const pageNumber = i + 1;
                                                // Show current page, first, last, and pages around current
                                                if (
                                                    pageNumber === 1 || 
                                                    pageNumber === pagination.pages || 
                                                    (pageNumber >= pagination.page - 1 && 
                                                    pageNumber <= pagination.page + 1)
                                                ) {
                                                    return (
                                                        <PaginationItem key={pageNumber}>
                                                            <PaginationLink
                                                                isActive={pageNumber === pagination.page}
                                                                onClick={() => handlePageChange(pageNumber)}
                                                            >
                                                                {pageNumber}
                                                            </PaginationLink>
                                                        </PaginationItem>
                                                    );
                                                } else if (
                                                    pageNumber === 2 || 
                                                    pageNumber === pagination.pages - 1
                                                ) {
                                                    return (
                                                        <PaginationItem key={pageNumber}>
                                                            <span className="px-3">...</span>
                                                        </PaginationItem>
                                                    );
                                                }
                                                return null;
                                            })}
                                            
                                            {pagination.page < pagination.pages && (
                                                <PaginationItem>
                                                    <PaginationNext 
                                                        onClick={() => handlePageChange(pagination.page + 1)} 
                                                    />
                                                </PaginationItem>
                                            )}
                                        </PaginationContent>
                                    </Pagination>
                                </div>
                            )}
                        </>
                    ) : !loading ? (
                        <Card className="shadow-sm">
                            <CardContent className="p-6">
                                <div className="text-center py-12">
                                    {searchTerm || showArchived ? (
                                        <>
                                            <h3 className="text-lg font-semibold mb-2">No matching submissions</h3>
                                            <p className="text-muted-foreground">
                                                Try adjusting your search or filter criteria.
                                            </p>
                                        </>
                                    ) : (
                                        <>
                                            <h3 className="text-lg font-semibold mb-2">No submissions yet</h3>
                                            <p className="text-muted-foreground">
                                                This form hasn't received any submissions.
                                            </p>
                                        </>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    ) : null}
                </motion.div>
            </motion.div>

            {/* Image Lightbox Dialog */}
            <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen} modal={true}>
                <DialogContent className="max-w-4xl p-0 bg-background border shadow-xl">
                    <div className="grid grid-cols-1 md:grid-cols-[1fr_220px]">
                        <div className="flex items-center justify-center h-[60vh] bg-black relative">
                            {lightboxImage && (
                                <img 
                                    src={lightboxImage.url} 
                                    alt={lightboxImage.alt} 
                                    className="max-w-full max-h-[60vh] object-contain" 
                                />
                            )}
                            <div className="absolute top-2 right-2 z-10 flex gap-2">
                                {lightboxImage && (
                                    <Button 
                                        variant="ghost" 
                                        size="icon" 
                                        className="h-8 w-8 bg-black/40 hover:bg-black/60 text-white"
                                        onClick={() => window.open(lightboxImage.url, '_blank')}
                                    >
                                        <FiExternalLink className="h-4 w-4" />
                                    </Button>
                                )}
                            </div>
                        </div>
                        
                        {lightboxImage?.file && (
                            <div className="p-4 border-l border-border flex flex-col h-[60vh] relative">
                                <Button 
                                    variant="ghost" 
                                    size="icon" 
                                    className="absolute top-2 right-2 h-8 w-8 bg-muted/50 hover:bg-muted/80"
                                    onClick={() => setLightboxOpen(false)}
                                >
                                    <FiX className="h-4 w-4" />
                                </Button>
                                
                                <div className="mt-8">
                                    <h3 className="text-lg font-medium mb-3 break-words pr-10">
                                        {lightboxImage.file.filename}
                                    </h3>
                                    
                                    <div className="space-y-3 mt-4">
                                        <div>
                                            <p className="text-xs text-muted-foreground">Type</p>
                                            <p className="text-sm">{lightboxImage.file.contentType}</p>
                                        </div>
                                        
                                        <div>
                                            <p className="text-xs text-muted-foreground">Size</p>
                                            <p className="text-sm">{(lightboxImage.file.size / 1024).toFixed(1)} KB</p>
                                        </div>
                                    </div>
                                    
                                    <div className="mt-auto pt-4">
                                        <Button 
                                            className="w-full"
                                            onClick={() => downloadFile(lightboxImage.file)}
                                        >
                                            <FiDownload className="mr-2 h-4 w-4" />
                                            Download
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>

            {/* Submission Details Dialog */}
            <Dialog open={submissionDetailsOpen} onOpenChange={(open) => open ? null : closeSubmissionDetails()} modal={true}>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                    {selectedSubmission && (
                        <div className="space-y-6">
                            {/* Clean Header */}
                            <div className="border-b pb-4">
                                <div className="flex items-start justify-between mb-4">
                                    <div>
                                        <h2 className="text-xl font-semibold text-foreground">Submission Details</h2>
                                        <p className="text-sm text-muted-foreground mt-1">
                                            Submitted {new Date(selectedSubmission.createdAt).toLocaleDateString('en-US', {
                                                weekday: 'long',
                                                year: 'numeric',
                                                month: 'long',
                                                day: 'numeric'
                                            })} at {new Date(selectedSubmission.createdAt).toLocaleTimeString('en-US', {
                                                hour: '2-digit',
                                                minute: '2-digit'
                                            })}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {selectedSubmission.archived && (
                                            <Badge variant="outline" className="border-orange-300 bg-orange-50 text-orange-700 dark:border-orange-700 dark:bg-orange-950/50 dark:text-orange-300">
                                                <FiArchive className="h-3 w-3 mr-1" />
                                                Archived
                                            </Badge>
                                        )}
                                    </div>
                                </div>
                                
                                {/* Metadata Row */}
                                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                                    <div className="flex items-center gap-1">
                                        <FiFileText className="h-3 w-3" />
                                        <span className="font-mono">{selectedSubmission.id}</span>
                                    </div>
                                    <div className="w-1 h-1 rounded-full bg-muted-foreground/40"></div>
                                    <div className="flex items-center gap-1">
                                        <FiClock className="h-3 w-3" />
                                        <span>{new Date(selectedSubmission.createdAt).toLocaleString()}</span>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Linked Tasks Section */}
                            {selectedSubmission.linkedTasks && selectedSubmission.linkedTasks.length > 0 && (
                                <div className="border-b pb-4">
                                    <h3 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                                        <FiPlus className="h-4 w-4" />
                                        Linked Tasks
                                    </h3>
                                    <div className="flex flex-wrap gap-3">
                                        {selectedSubmission.linkedTasks.map((linkedTask, index) => (
                                            <TaskLink 
                                                key={index}
                                                taskKey={linkedTask.taskKey}
                                                summary={linkedTask.taskSummary}
                                                organizationSlug={organizationSlug}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}
                            
                            {/* Form Fields */}
                            {getDisplayableComponents().map(component => (
                                <div key={component._id} className="border-b pb-4 last:border-b-0">
                                    <h3 className="text-sm font-medium text-muted-foreground mb-1 flex items-center gap-2">
                                        {component.name}
                                        <span className="text-xs px-1.5 py-0.5 rounded-sm bg-muted/70 text-muted-foreground font-normal">
                                            {component.type}
                                        </span>
                                    </h3>
                                    <div>
                                        {formatValueForDetails(
                                            selectedSubmission.data[component.name],
                                            component
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Archive Dialog */}
            <Dialog open={archiveDialogOpen} onOpenChange={setArchiveDialogOpen} modal={true}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <FiArchive className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                            Archive Submission
                        </DialogTitle>
                        <DialogDescription>
                            This submission will be archived and hidden from the main list.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4">
                        <div className="flex items-center space-x-2">
                            <div className="h-5 w-1.5 rounded-sm bg-orange-500"></div>
                            <span className="text-sm font-medium text-orange-600 dark:text-orange-400">Archive Action</span>
                        </div>
                        
                        <div>
                            <label className="text-sm font-medium mb-2 block">
                                Reason (optional)
                            </label>
                            <Textarea
                                placeholder="e.g., Duplicate submission, Spam, Invalid data..."
                                value={archiveReason}
                                onChange={(e) => setArchiveReason(e.target.value)}
                                rows={3}
                                className="resize-none"
                            />
                        </div>
                    </div>
                    
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setArchiveDialogOpen(false)}>
                            Cancel
                        </Button>
                        <Button 
                            onClick={handleArchiveConfirm}
                        >
                            <FiArchive className="mr-2 h-4 w-4" />
                            Archive
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Create Task from Submission Dialog */}
            <CreateTaskFromSubmissionDialog 
                open={createTaskFromSubmissionOpen}
                onOpenChange={setCreateTaskFromSubmissionOpen}
                submission={selectedSubmissionForTask}
                formSlug={formSlug}
                onTaskCreated={(task) => {
                    toast({
                        description: `Task ${task.key} created successfully`,
                    });
                    // Refresh submissions to show any linked task indicators
                    fetchSubmissions(pagination.page);
                }}
            />
        </Layout>
    );
};

export default FormSubmissions; 